import { useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';

import { recentHomeSearchKeywordListState } from '@/recoil/atoms/search';
import { HomeSearchService } from '@/services/search/HomeSearchService';
import { HomeSearchKeywordList } from '@/types/search';
import { isWebView } from '@/utils/mobile/RNPlugins';
import { nativePostMessageEventList } from '@/utils/mobile/postMessageEventList.messages';

interface RecentKeywordStorage {
  recentKeywordViewData: HomeSearchKeywordList;
  addRecentKeyword: (keyword: string) => Promise<void>;
  removeRecentKeyword: (keyword: string) => Promise<void>;
  removeAllRecentKeyword: () => Promise<void>;
}

const useRecentKeywords = (): RecentKeywordStorage => {
  const [recentHomeSearchKeywordList, setRecentHomeSearchKeywordList] = useRecoilState(
    recentHomeSearchKeywordListState
  );

  const addRecentKeyword = async (keyword: string) => {
    if (isWebView()) {
      await nativePostMessageEventList.addRecentKeyword(keyword);
      await nativePostMessageEventList.refreshRecentKeywordList();
    } else {
      const newKeywordList = HomeSearchService.getNewKeywordList([...recentHomeSearchKeywordList], keyword);
      setRecentHomeSearchKeywordList(newKeywordList);
    }
  };

  const removeRecentKeyword = async (keyword: string) => {
    if (isWebView()) {
      await nativePostMessageEventList.removeRecentKeyword(keyword);
      await nativePostMessageEventList.refreshRecentKeywordList();
    } else {
      const newKeywordList = recentHomeSearchKeywordList.filter((recentKeyword) => recentKeyword !== keyword);
      setRecentHomeSearchKeywordList(newKeywordList);
    }
  };

  const removeAllRecentKeyword = async () => {
    if (isWebView()) {
      await nativePostMessageEventList.removeAllRecentKeyword();
      await nativePostMessageEventList.refreshRecentKeywordList();
    } else {
      setRecentHomeSearchKeywordList([]);
    }
  };

  const getRecentKeywordList = async () => {
    if (isWebView()) {
      return await nativePostMessageEventList.getRecentKeywordList<HomeSearchKeywordList>();
    }
    return recentHomeSearchKeywordList;
  };

  const recentKeywordViewData = useMemo(() => {
    let recentKeywordViewData: HomeSearchKeywordList = recentHomeSearchKeywordList;
    if (isWebView()) {
      (async () => {
        recentKeywordViewData = await getRecentKeywordList();
      })();
    }
    return recentKeywordViewData;
  }, [recentHomeSearchKeywordList]);

  // 웹뷰에서 Native 초기 데이터 로드 및 메시지 리스너 설정
  useEffect(() => {
    if (!isWebView()) return;

    const initWebViewKeywords = async () => {
      const keywords = await getRecentKeywordList();
      setRecentHomeSearchKeywordList(keywords);
    };
    initWebViewKeywords();

    const handleRefreshRecentKeywords = async (event: MessageEvent) => {
      try {
        const messageData = JSON.parse(event.data);
        if (messageData.type === 'REFRESH_RECENT_KEYWORD_LIST') {
          const updatedKeywords = messageData.data || (await getRecentKeywordList());
          setRecentHomeSearchKeywordList(updatedKeywords);
        }
      } catch (error) {
        console.log('Failed to parse message data:', error);
      }
    };

    const handleDocumentMessage = (event: Event) => {
      if ('data' in event) {
        handleRefreshRecentKeywords(event as MessageEvent);
      }
    };

    window.addEventListener('message', handleRefreshRecentKeywords);
    document.addEventListener('message', handleDocumentMessage);

    return () => {
      window.removeEventListener('message', handleRefreshRecentKeywords);
      document.removeEventListener('message', handleDocumentMessage);
    };
  }, []);

  return {
    recentKeywordViewData,
    addRecentKeyword,
    removeRecentKeyword,
    removeAllRecentKeyword,
  };
};

export default useRecentKeywords;
